import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Input, Segmented, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ProductDTO } from "../../api/product";
import { AssemblyVersionClient, BomClient, formatNumber, ManufacturingPartClient, refusalMessage, StockBalanceClient } from "../../api/manufacturing";
import { ON_THE_FLOOR, ProductionOrderClient } from "../../api/production";
import { OpenRecord } from "../../components/OpenRecord";
import { productionStatusLabel } from "../production/badges";
import { PartBomTab } from "../manufacturing/PartBomTab";
import { useReference } from "../manufacturing/PartDetailPage";
import { PartFilesPanel } from "../manufacturing/PartFilesPanel";

/** One part of the product's structure, as the tab shows it. */
interface StructurePart {
  key: string;
  label: string;
  id: number;
  number: string;
  name: string;
  /** Boxes the version's SKU ships in; none for the semi-finished. */
  boxes?: number;
}

/**
 * How the plant makes this product (0066), as two tabs of the product page.
 * Fabrication is its semi-finished: what Laval makes, the fabrication BOM.
 * Assembly is its versions: how Terrebonne boxes it, one per sellable SKU,
 * with its BOM in box sections. Each shows its part's plant stock, open
 * orders, files and the part's own BOM editor (PartBomTab, the same one as
 * on the part page, revisions included).
 *
 * <p>Until cutover the go-live script rebuilds the structure from SIGM's box
 * and U parts on every run, which rewrites the assembly BOMs.</p>
 */
export function ManufacturingTab({ draft, side }: { draft: ProductDTO; side: "fabrication" | "assembly" }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new AssemblyVersionClient(http), [http]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["manufacturing", "product-structure", draft.id],
    queryFn: () => client.ofProduct(draft.id),
    staleTime: 60_000,
  });
  const parts = useMemo<StructurePart[]>(() => {
    if (!data) return [];
    const list: StructurePart[] = [];
    if (side === "fabrication" && data.semi_finished_part_id != null) {
      list.push({
        key: "semi-finished",
        label: t("product.manufacturing.fabrication", "Fabrication"),
        id: data.semi_finished_part_id,
        number: data.semi_finished_number ?? "",
        name: t("product.manufacturing.semi-finished", "Semi-finished"),
      });
    }
    for (const version of side === "assembly" ? data.versions : []) {
      list.push({
        key: `version-${String(version.id)}`,
        label: version.code,
        id: version.output_part_id,
        number: version.output_part_number ?? "",
        name: version.output_sku ?? "",
        boxes: version.box_count,
      });
    }
    return list;
  }, [data, t]);
  const [chosen, setChosen] = useState<string | null>(null);
  const current = parts.find((part) => part.key === chosen) ?? parts[0];

  if (isLoading) {
    return <p className="px-4 py-3 text-[13px] text-ink-muted lg:px-6">{t("common.loading", "Loading…")}</p>;
  }
  // A failed load is not "not made here": saying so would send someone to fix a structure that is fine.
  if (isError) {
    return <p className="px-4 py-3 text-[13px] text-risk lg:px-6">{t("product.manufacturing.error", "The linked part could not be loaded.")}</p>;
  }
  if (!current) {
    return (
      <div className="px-4 py-3 text-[13px] text-ink-muted lg:px-6">
        {draft.type === "BUNDLE"
          ? t("product.manufacturing.none-bundle", "A bundle is made as its items: open an item to see its part.")
          : side === "fabrication"
            ? <SemiFinishedLink productId={draft.id} />
            : t("product.manufacturing.none.structure", "This product has no assembly version: the SIGM part import gives it one from part 90-{{sku}} when it exists.", {
                sku: draft.metadata?.sku ?? "",
              })}
      </div>
    );
  }
  return (
    <LinkedPart
      key={current.key}
      part={current}
      picker={
        parts.length > 1 ? (
          <Segmented value={current.key} onChange={setChosen} options={parts.map((part) => ({ value: part.key, label: part.label }))} />
        ) : null
      }
    />
  );
}

function LinkedPart({ part, picker }: { part: StructurePart; picker: React.ReactNode }) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const balances = useMemo(() => new StockBalanceClient(http), [http]);
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  const boms = useMemo(() => new BomClient(http), [http]);

  const stock = useQuery({
    queryKey: ["manufacturing", "stock-balance", "part", part.id],
    queryFn: () => balances.find({ page: 0, size: 50, filters: { part_id: [String(part.id)] }, sort: ["warehouse_code,asc"] }),
  });
  const open = useQuery({
    queryKey: ["production", "production-order", "open", "part", part.id],
    queryFn: () =>
      orders.find({
        page: 0,
        size: 20,
        filters: { part_id: [String(part.id)], status: ["PLANNED", ...ON_THE_FLOOR] },
        sort: ["number,desc"],
      }),
  });
  const explosion = useQuery({
    queryKey: ["manufacturing", "bom", "explode", part.id, 1],
    queryFn: () => boms.explode(part.id, 1),
    staleTime: 60_000,
  });

  const [view, setView] = useState<"bom" | "files">("bom");
  // The product's part and every component in its BOM: their drawings are the product's.
  const fileParts = useMemo(
    () => [
      { id: part.id, number: part.number ?? "" },
      ...(explosion.data ?? []).map((line) => ({ id: line.part_id, number: line.part_number })),
    ],
    [part.id, part.number, explosion.data],
  );

  const balanceRows = (stock.data?.content ?? []).filter((row) => Number(row.quantity) !== 0);
  const openOrders = open.data?.content ?? [];
  const moreOrders = Math.max(0, (open.data?.total ?? 0) - openOrders.length);

  // The part's own BOM editor, as on the part page: it needs the part and the reference lists.
  const detail = useQuery({
    queryKey: ["manufacturing", "part", "detail", part.id],
    queryFn: () => new ManufacturingPartClient(http).detail(part.id),
    staleTime: 60_000,
  });
  const reference = useReference(http, true);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2 lg:px-6">
        {picker}
        {picker && <span className="mx-1 h-4 w-px bg-line" />}
        <span className="text-[13px] font-medium">{part.number}</span>
        <span className="text-[13px] text-ink-muted">{part.name}</span>
        <OpenRecord target={{ kind: "part", id: part.id, tab: "bom" }} label={part.number ?? ""} />
        {part.boxes != null && part.boxes > 0 && (
          <Badge size="sm" variant="neutral">
            {t("product.manufacturing.boxes", "{{count}} boxes", { count: part.boxes })}
          </Badge>
        )}
        <span className="mx-1 h-4 w-px bg-line" />
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "bom", label: t("product.manufacturing.view.bom", "Bill of materials") },
            { value: "files", label: t("product.manufacturing.view.files", "Files") },
          ]}
        />
        <span className="mx-1 h-4 w-px bg-line" />
        {stock.isLoading ? null : balanceRows.length === 0 ? (
          <span className="text-[12px] text-ink-muted">{t("product.manufacturing.no-stock", "No plant stock")}</span>
        ) : (
          balanceRows.map((row) => (
            <Badge key={row.warehouse_id} size="sm" variant={Number(row.quantity) < 0 ? "error" : "neutral"}>
              {row.warehouse_code}: {formatNumber(row.quantity)}
            </Badge>
          ))
        )}
      </div>

      {openOrders.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2 lg:px-6">
          <span className="text-[12px] text-ink-muted">{t("product.manufacturing.open-orders", "Open production orders")}</span>
          {openOrders.map((order) => (
            <span key={order.id} className="inline-flex items-center gap-1">
              <Badge size="sm" variant={order.status === "PLANNED" ? "neutral" : "info"}>
                #{order.number} · {productionStatusLabel(t, order.status)} · {formatNumber(order.quantity)}
                {order.due_date ? ` · ${order.due_date}` : ""}
              </Badge>
              <OpenRecord target={{ kind: "production-order", id: order.id }} label={`#${String(order.number)}`} />
            </span>
          ))}
          {moreOrders > 0 && (
            <span className="text-[12px] text-ink-muted">{t("product.manufacturing.more-orders", "and {{n}} more", { n: moreOrders })}</span>
          )}
        </div>
      )}

      {view === "files" ? (
        <PartFilesPanel parts={fileParts} uploadPartId={part.id} readOnly={!canWrite} />
      ) : (
        detail.data ? (
          <PartBomTab
            part={detail.data.part}
            units={reference.units ?? []}
            operations={reference.operations ?? []}
            departments={reference.departments ?? []}
            showRouting={false}
            readOnly={!canWrite}
          />
        ) : (
          <p className="px-4 py-3 text-[13px] text-ink-muted lg:px-6">{t("common.loading", "Loading…")}</p>
        )
      )}
    </div>
  );
}

/** No semi-finished yet: say so, and link one (a part of a semi-finished group no product uses). */
function SemiFinishedLink({ productId }: { productId: number }) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new AssemblyVersionClient(http), [http]);
  const [search, setSearch] = useState("");
  const [partId, setPartId] = useState("");
  const candidates = useQuery({
    queryKey: ["manufacturing", "structure-candidates", "SEMI_FINISHED", search],
    queryFn: () => client.candidates("SEMI_FINISHED", search),
    enabled: canWrite,
  });
  const link = useMutation({
    mutationFn: () => client.setSemiFinished(productId, Number(partId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["manufacturing"] }),
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <span className="flex flex-col gap-2">
      <span>{t("product.manufacturing.none.fabrication", "This product has no semi-finished: it is not made at Laval, or the structure build has not given it one yet.")}</span>
      {canWrite && (
        <span className="flex flex-wrap items-center gap-2">
          <Input aria-label={t("assembly.version.add.search", "Find the part")} className="w-48" value={search}
            placeholder={t("product.manufacturing.semi-finished.search", "91-")} onChange={(event) => setSearch(event.target.value)} />
          <Select aria-label={t("column.part.number", "Part")} className="w-80" value={partId}
            placeholder={t("product.manufacturing.semi-finished.pick", "A semi-finished no product uses")}
            options={(candidates.data ?? []).map((part) => ({ value: String(part.id), label: `${part.number} · ${part.name}` }))}
            onChange={(event) => setPartId(event.target.value)} />
          <Button size="sm" variant="primary" disabled={partId === ""} loading={link.isPending} onClick={() => link.mutate()}>
            {t("product.manufacturing.semi-finished.link", "Link it")}
          </Button>
        </span>
      )}
    </span>
  );
}
